/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import {
  DataMaskStateWithId,
  DataRecordFilters,
  JsonObject,
  PartialFilters,
} from '@superset-ui/core';
import { ChartConfiguration, ChartQueryPayload } from 'src/dashboard/types';
import { getExtraFormData } from 'src/dashboard/components/nativeFilters/utils';
import { areObjectsEqual } from 'src/reduxUtils';
import getEffectiveExtraFilters from './getEffectiveExtraFilters';
import { getAllActiveFilters } from '../activeAllDashboardFilters';

// We cache formData objects so that our connected container components don't always trigger
// render cascades. we cannot leverage the reselect library because our cache size is >1
const cachedFiltersByChart = {};
const cachedFormdataByChart = {};
// Cache for chart.form_data to detect actual changes
const cachedChartFormData = {};
// Cache hash for quick comparison
const cachedHashByChart = {};

// Generate a quick hash for comparison
function generateCacheKey(
  chart: ChartQueryPayload,
  filters: DataRecordFilters,
): string {
  // Use chart ID and all filter keys for accurate comparison
  // This prevents cache key collisions
  const filterKeys = Object.keys(filters || {}).sort();
  return `${chart?.id}_${filterKeys.length}_${filterKeys.join('_')}`;
}

export interface GetFormDataWithExtraFiltersArguments {
  chartConfiguration: ChartConfiguration;
  chart: ChartQueryPayload;
  filters: DataRecordFilters;
  colorScheme?: string;
  colorNamespace?: string;
  sliceId: number;
  dataMask: DataMaskStateWithId;
  nativeFilters: PartialFilters;
  extraControls: Record<string, string | boolean | null>;
  labelColors?: Record<string, string>;
  sharedLabelColors?: Record<string, string>;
  allSliceIds: number[];
  /** Dashboard-level cache timeout override (seconds); -1 bypasses cache */
  dashboardCacheTimeout?: number | null;
}

// this function merge chart's formData with dashboard filters value,
// and generate a new formData which will be used in the new query.
// filters param only contains those applicable to this chart.
export default function getFormDataWithExtraFilters({
  chart,
  filters,
  nativeFilters,
  chartConfiguration,
  colorScheme,
  colorNamespace,
  sliceId,
  dataMask,
  extraControls,
  labelColors,
  sharedLabelColors,
  allSliceIds,
  dashboardCacheTimeout,
}: GetFormDataWithExtraFiltersArguments) {
  // Quick check using cache key to avoid expensive comparisons
  const cacheKey = generateCacheKey(chart, filters);
  const cachedFormData = cachedFormdataByChart[sliceId];
  const cachedHash = cachedHashByChart[sliceId];

  // If cache key matches and we have cached data, do a quick check
  if (cachedHash === cacheKey && cachedFormData) {
    // Quick reference checks for simple values
    if (
      cachedFormData?.color_scheme === colorScheme &&
      cachedFormData?.color_namespace === colorNamespace &&
      cachedFormData?.cache_timeout === dashboardCacheTimeout &&
      cachedFiltersByChart[sliceId] === filters &&
      cachedChartFormData[sliceId] === chart?.form_data
    ) {
      // Only do deep comparison for objects that might have changed
      if (
        areObjectsEqual(cachedFormData?.label_colors, labelColors, {
          ignoreUndefined: true,
        }) &&
        areObjectsEqual(
          cachedFormData?.shared_label_colors,
          sharedLabelColors,
          { ignoreUndefined: true },
        ) &&
        areObjectsEqual(cachedFormData?.dataMask, dataMask, {
          ignoreUndefined: true,
        }) &&
        areObjectsEqual(cachedFormData?.extraControls, extraControls, {
          ignoreUndefined: true,
        })
      ) {
        return cachedFormData;
      }
    }
  }

  let extraData: { extra_form_data?: JsonObject } = {};
  const activeFilters = getAllActiveFilters({
    chartConfiguration,
    dataMask,
    nativeFilters,
    allSliceIds,
  });
  const filterIdsAppliedOnChart = Object.entries(activeFilters)
    .filter(([, { scope }]) => scope.includes(chart.id))
    .map(([filterId]) => filterId);
  if (filterIdsAppliedOnChart.length) {
    extraData = {
      extra_form_data: getExtraFormData(dataMask, filterIdsAppliedOnChart),
    };
  }

  const formData = {
    ...chart.form_data,
    label_colors: labelColors,
    shared_label_colors: sharedLabelColors,
    ...(colorScheme && { color_scheme: colorScheme }),
    ...(dashboardCacheTimeout != null && {
      cache_timeout: dashboardCacheTimeout,
    }),
    extra_filters: getEffectiveExtraFilters(filters),
    ...extraData,
    ...extraControls,
  };

  // Update caches
  cachedFiltersByChart[sliceId] = filters;
  cachedFormdataByChart[sliceId] = { ...formData, dataMask, extraControls };
  cachedChartFormData[sliceId] = chart?.form_data;
  cachedHashByChart[sliceId] = cacheKey;

  return formData;
}

// Clear cache for a specific chart (call when chart is deleted)
export function clearFormDataCache(sliceId: number) {
  delete cachedFiltersByChart[sliceId];
  delete cachedFormdataByChart[sliceId];
  delete cachedChartFormData[sliceId];
  delete cachedHashByChart[sliceId];
}

// Clear all caches (call when dashboard is unloaded)
export function clearAllFormDataCache() {
  Object.keys(cachedFiltersByChart).forEach(key => {
    delete cachedFiltersByChart[key];
    delete cachedFormdataByChart[key];
    delete cachedChartFormData[key];
    delete cachedHashByChart[key];
  });
}
